import React, { useEffect } from 'react';
import { Button } from 'app/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'app/components/ui/dialog';
import { Form } from 'app/components/ui/form';
import { useForm } from 'react-hook-form';
import { useToast } from 'app/components/ui/use-toast';
import { CategoriesForm } from '../form';
import { useProductsSlice } from '../../slice';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

// Validation schema
const categorySchema = z.object({
  id: z.number(),
  category_name: z
    .string()
    .min(2, 'Category name must be at least 2 characters')
    .max(100, 'Category name must be less than 100 characters'),
  slug: z
    .string()
    .min(2, 'Slug must be at least 2 characters')
    .regex(
      /^[a-z0-9-]+$/,
      'Slug can only contain lowercase letters, numbers, and hyphens',
    ),
  parent_category_id: z.string().optional(),
  image: z.string().optional(),
});

type CategoryFormData = z.infer<typeof categorySchema>;

interface Props {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  Categoriesdata: any;
}

export function DialogUpdateProductCategories({
  isOpen,
  onOpenChange,
  Categoriesdata,
}: Props) {
  const { useUpdateProductsCategoriesMutation } = useProductsSlice();
  const [updateCategory, { isLoading, isError, error, data }] =
    useUpdateProductsCategoriesMutation();
  const { toast } = useToast();

  const form = useForm<CategoryFormData>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      id: 0,
      category_name: '',
      slug: '',
      parent_category_id: '0',
      image: '',
    },
  });

  // Set form values when category data is provided
  useEffect(() => {
    if (Categoriesdata && isOpen) {
      form.reset({
        id: Categoriesdata.category_id,
        category_name: Categoriesdata.category_name || '',
        slug: Categoriesdata.slug || '',
        parent_category_id: Categoriesdata.parent_category_id
          ? Categoriesdata.parent_category_id.toString()
          : '0',
        image: Categoriesdata.image || '',
      });
    }
  }, [Categoriesdata, isOpen, form]);

  // Handle successful update
  useEffect(() => {
    if (data && !isError) {
      toast({
        title: 'Success',
        description: data?.message || 'Category updated successfully',
        variant: 'default',
      });
      onOpenChange(false);
    }
  }, [data, isError, onOpenChange, toast]);

  // Handle errors
  useEffect(() => {
    if (isError && error) {
      const errorMessage =
        'data' in error && error.data?.message
          ? error.data.message
          : 'Failed to update category. Please try again.';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  }, [isError, error, toast]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      form.reset();
    }
  }, [isOpen, form]);

  const onSubmit = async (formData: CategoryFormData) => {
    try {
      const payload = {
        id: formData.id,
        category_name: formData.category_name.trim(),
        slug: formData.slug.trim(),
        parent_category_id:
          formData.parent_category_id === '0'
            ? null
            : parseInt(formData.parent_category_id || '0'),
        image: formData.image || null,
      };

      await updateCategory(payload).unwrap();
    } catch (error) {
      // Error handling is done in useEffect
      console.error('Category update error:', error);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            Edit Category
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <CategoriesForm />

            <DialogFooter className="gap-2 px-5 pb-5">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="min-w-[100px]"
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
